import React from 'react'
import Link from 'next/link'
import { LinkPart } from '@/utils/ui/types/linked-content'

interface LinkedTextProps {
  content: {
    [key: string]: string | LinkPart
  }
  className?: string
}

/**
 * LinkedText component for rendering text with embedded links
 *
 * Expects an object with parts (part1, part2, etc.) and links (link1, link2, etc.)
 * Example:
 * {
 *   part1: "This is some text with a ",
 *   link1: { text: "link", href: "/some-page" },
 *   part2: " in the middle of it."
 * }
 */
const LinkedText: React.FC<LinkedTextProps> = ({ content, className = '' }) => {
  // If content is not provided or is empty, return null
  if (!content || Object.keys(content).length === 0) return null

  // Ensure we have a stable order for keys to prevent hydration mismatches
  const sortedKeys = Object.keys(content).sort((a, b) => {
    const aMatch = a.match(/^(part|link)(\d+)$/)
    const bMatch = b.match(/^(part|link)(\d+)$/)

    if (!aMatch || !bMatch) return a.localeCompare(b)

    const aType = aMatch[1]
    const bType = bMatch[1]
    const aNum = parseInt(aMatch[2])
    const bNum = parseInt(bMatch[2])

    // If the numbers are different, sort by number
    if (aNum !== bNum) return aNum - bNum

    // If the numbers are the same, put 'part' before 'link'
    return aType === 'part' ? -1 : 1
  })

  // Convert the content to a simple array of elements for stable rendering
  const elements = sortedKeys.map((key) => {
    const value = content[key]

    if (key.startsWith('link') && typeof value !== 'string') {
      const linkData = value as LinkPart
      return (
        <Link
          key={key}
          href={linkData.href}
          className='text-indigo-600 hover:underline'
        >
          {linkData.text}
        </Link>
      )
    }

    if (key.startsWith('part') && typeof value === 'string') {
      return <React.Fragment key={key}>{value}</React.Fragment>
    }

    return null
  })

  return <span className={className}>{elements}</span>
}

export default LinkedText
